/// <amd-module name="DistributedTaskUI/Resources/ResourcesHub" />
import * as React from "react";

import * as Constants from "Constants";
import { ResourceFilters } from "DistributedTask/Client/Contracts/TaskAgent";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import * as Resources from "DistributedTaskUI/Resources/Resources";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { ServiceEndpointsHub } from "DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointsHub";
import { ServiceEndpointStore } from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore";
import { IResourcesHubState, ResourceItemExtended } from "ResourcesHub.Model";
import { ResourcesSource } from "ResourcesSource";
import { ResourcesStore } from "ResourcesStore";

import { CheckIssueType } from "Checks/Client/Contracts/PipelinesChecks";
import { PerformanceConstants } from "DistributedTask/Common/SharedResources/Constants";
import { SecurityNamespaceConstants } from "DistributedTaskUI/SecurityComponent/Constants";
import * as RoleAssignmentComponentSecurityControl from "DistributedTaskUI/SecurityComponent/UserPermissionsAuthorization/Components/RoleAssignmentComponentSecurityControl";
import { ResourcesActionsCreator } from "ResourcesActionsCreator";
import { ResourcesActionsHub } from "ResourcesActionsHub";
import { getTFSPageData } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import * as Utils_String from "VSS/Core/Util/String";
import { format, localeFormat } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureEnabled, isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { HistoryServiceEventNames, INavigationStateChangedEvent, IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Card } from "VSSUI/Card";
import { DropdownFilterBarItem, } from "VSSUI/Dropdown";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Pill, PillSize } from "VSSUI/Pill";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { ITableColumn, ITableRow, SimpleTableCell, Table, TableColumnStyle } from "VSSUI/Table";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Tooltip } from "VSSUI/TooltipEx";
import { DropdownMultiSelection } from "VSSUI/Utilities/DropdownSelection";
import { FILTER_APPLIED_EVENT, Filter, IFilterState } from "VSSUI/Utilities/Filter";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";

interface IFilter {
    identities: { [key: string]: IdentityRef };
}

class ResourcesHub extends VssComponent<{}, IResourcesHubState>{
    private _workloadIdentityFederationEnabled: boolean = false;
    private _allowCreatingEndpointsInDraftStateEnabled: boolean = false;

    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);
        this._workloadIdentityFederationEnabled = isFeatureEnabled(context.pageContext, "ms.vss-distributedtask-web.workload-identity-federation", false);
        this._allowCreatingEndpointsInDraftStateEnabled = isFeatureFlagEnabled(context.pageContext, "ServiceEndpoints.AllowCreatingEndpointsInDraftState", false);

        this._historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._ttiMarked = false;
        this._viewType = new ObservableValue(Constants.ViewType.Hub);
        this._actionsHub = new ResourcesActionsHub();
        this._resourcesSource = new ResourcesSource(this.context.pageContext);
        this._actionCreator = new ResourcesActionsCreator(this._actionsHub, this._resourcesSource);
        this._store = new ResourcesStore(this._actionsHub);
        this._actionCreator.initializeList(false);
        this._serviceEndpointStore = StoreManager.GetStore<ServiceEndpointStore>(ServiceEndpointStore, ServiceEndpointStore.getKey());
        this._serviceEndpointActionCreator = ActionCreatorManager.CreateActionCreator<ServiceEndpointActionsCreator, IVssPageContext>
                                            (ServiceEndpointActionsCreator, "service-endpoints-section-actionscreator", this.context.pageContext);
        this._expandPanel = new ObservableValue(false);
        this._selectedResourceIndex = -1;
        if (this._store) {
            this.state = this._store.getState();
        } else {
            this.state = {
                resources: [],
                dataLoaded: false,
                filters: null,
                filterOptions: null,
                currentState: "",
                filterBarVisibility: true,
                viewType: Constants.ViewType.Hub,
                selectedResourceIndex: -1,
                continuationToken: "",
                hasProjectLevelManagePermission: true
            };
        }

        this._resourcesProvider = new ArrayItemProvider<ResourceItemExtended>(this.state.resources);
        this._selectionMultiList = new DropdownMultiSelection();
    }

    public render(): React.ReactNode {
        return (
            <Surface background={SurfaceBackground.neutral}>
                {this._getPageContent()}
            </Surface>
        );
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
        if (!this._ttiMarked && this.state.dataLoaded) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            if (this._viewType.value === Constants.ViewType.Hub) {
                performanceService.markTimeToInteractive(PerformanceConstants.ResourcesHubFeature, PerformanceConstants.ResourcesHubScenario);
            }

            this._ttiMarked = true;
        }
    }

    public componentDidUpdate(): void {
        if (!this._ttiMarked && this.state.dataLoaded) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            if (this._viewType.value === Constants.ViewType.Hub) {
                performanceService.markTimeToInteractive(PerformanceConstants.ResourcesHubFeature, PerformanceConstants.ResourcesHubScenario);
            }

            this._ttiMarked = true;
        }
    }

    public UNSAFE_componentWillMount(): void {
        this.state = this._store.getState();
        this._historyService.subscribe(this._onHistoryPopped, HistoryServiceEventNames.statePopped);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
        this._historyService.unsubscribe(this._onHistoryPopped, HistoryServiceEventNames.statePopped);
    }

    private _onStoreChanged = (): void => {
        let state = this._store.getState();
        this._resourcesProvider = new ArrayItemProvider<ResourceItemExtended>(state.resources);
        state.filterOptions.identities.forEach((identity) => {
            if (identity && !this._filterDefinition.identities[identity.id]) {
                this._filterDefinition.identities[identity.id] = identity;
            }
        });

        this._setfilterOptions(state);
        this.setState(state);
    }

    private _getPageContent(): JSX.Element {
        const resourceId = this._getResourceIdFromUrl();
        if (resourceId) {
            this._viewType.value = Constants.ViewType.ServiceEndpoint;
        }

        if (!this.state.dataLoaded) {
            return this._getLoadingDataComponent();
        }

        const shouldShowZeroData = (!this.state.resources || this.state.resources.length === 0) && this._areFiltersEmpty();
        return (
            <React.Fragment>
                {shouldShowZeroData && this._getZeroDataComponent()}
                {!shouldShowZeroData && this._getLoadedDataContent(resourceId)}
                {this._getSidePanel()}
            </React.Fragment>
        );
    }

    private _getLoadedDataContent(resourceId: string): JSX.Element {
        return (
            <Observer viewType={this._viewType}>
                {
                    (props: {viewType: Constants.ViewType}) => {
                        if (props.viewType === Constants.ViewType.ServiceEndpoint) {
                            if (resourceId) {
                                let index = this.state.resources.findIndex(resource => resource.id === resourceId);
                                if (index === -1) {
                                    return this._getNoManagePermissionCardComponent(resourceId);
                                }
                            }
                            return this._getServiceEndpointView(this._selectedResourceIndex, resourceId);
                        } else if (props.viewType === Constants.ViewType.SecurityTab) {
                            return this._getProjectLevelSecurityView();
                        }

                        return this._getResourcesListView();
                    }
                }
            </Observer>
        );
    }

    private _getSidePanel(): JSX.Element {
        return (<Observer isPanelOpen={this._expandPanel}>
            {
                (props: { isPanelOpen: boolean }) => {
                    if (props.isPanelOpen) {
                        return this._getNewServiceConnectionPanel();
                    }

                    return null;
                }
            }
        </Observer>);
    }

    private _getNoManagePermissionCardComponent(resourceId: any): JSX.Element {
        return (
            <MessageCard className="cant-manage-message-card" severity={MessageCardSeverity.Error}>
                {localeFormat(Resources.ServiceEndpointNotFoundMessage, resourceId)}
            </MessageCard>
        );
    }

    private _getServiceConnectionUpgradeAvailable(): JSX.Element {
        if (!this._workloadIdentityFederationEnabled) {
            return null;
        }
        const hasAtLeastOneUpgradeAvailable = !!this.state.resources.find(x => x.properties["isSchemeUpgradeAvailable"]);
        if (!hasAtLeastOneUpgradeAvailable) {
            return null;
        }

        return (
            <MessageCard
                className="margin-bottom-16"
                iconProps={{
                    iconName: "Shield"
                }}
                buttonProps={[
                    {
                        text: Resources.LearnMoreText,
                        target: "_blank",
                        href: "https://aka.ms/azdo-rm-workload-identity/"
                    }
                ]}>
                {Resources.ServiceConnectionUpgradeAvailableBannerText}
            </MessageCard>
        );
    }

    private _getZeroDataComponent(): JSX.Element {
        const image = getAssetLocation(this.context.pageContext, "ms.vss-work-web/my-work-content/Content/zerodata-your-work-in-one-place.svg");

        return (
            <Page className="flex-grow">
                <ZeroData
                    primaryText={Resources.ZeroDataPrimaryText}
                    imagePath={image}
                    imageAltText={"Image"}
                    secondaryText={Resources.ZeroDataSecondaryText}
                    actionText={Resources.ZeroDataActionText}
                    actionType={ZeroDataActionType.ctaButton}
                    onActionClick={() => {  this._expandPanel.value = true; }}
                />
            </Page>
        );
    }

    private _getLoadingDataComponent(): JSX.Element {
        return (
            <Page className="flex-grow">
                <Spinner
                    ariaLive="assertive"
                    className="loading-component"
                    size={SpinnerSize.large}
                    label={Resources.LoadingLabel} />
            </Page>
        );
    }

    private _getServiceEndpointView(selectedIndex: number, resourceId: string): JSX.Element {
        let serviceEndpointsComponentProps;
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario(PerformanceConstants.ServiceEndpointOverviewScenario, true);

        if (!resourceId) {
            const selectedEndpoint = this.state.resources[selectedIndex];
            serviceEndpointsComponentProps = {
                id: selectedEndpoint.id,
                name: selectedEndpoint.name,
                typeIconUrl: selectedEndpoint.iconUrl,
                store: this._serviceEndpointStore,
                onBackNavigation: this._onBackNavigation
            };

            this._serviceEndpointActionCreator.getServiceEndpoint(selectedEndpoint.id);
            this._setUrl(selectedEndpoint.id);
        } else {
            serviceEndpointsComponentProps = {
                id: resourceId,
                store: this._serviceEndpointStore,
                onBackNavigation: this._onBackNavigation
            };

            this._serviceEndpointActionCreator.getServiceEndpoint(resourceId);
            this._setUrl(resourceId);
        }

        return (
            <ServiceEndpointsHub {...serviceEndpointsComponentProps} />
        );
    }

    private _getFilterBar = (): JSX.Element => {
        let identities = this._getCreatedByListItems();
        if (this.state.filterBarVisibility && this._filter) {
            return (
                <FilterBar className={"filter-bar depth-8"} filter={this._filter}>
                    <KeywordFilterBarItem filterItemKey="keyword" />
                    <DropdownFilterBarItem
                        placeholder={Resources.CreatedByFilterPlaceHolder}
                        filterItemKey="createdByFilter"
                        showFilterBox={true}
                        selection={this._selectionMultiList}
                        items={this._getIdentityPickerItems(identities)}
                    />
                </FilterBar>
            );
        }
    }

    private _getIdentityPickerItems(identities: IdentityRef[]): IListBoxItem<IdentityRef>[] {
        let options: IListBoxItem<IdentityRef>[] = [];
        identities.map((identity) => {
            options.push({
                id: identity.id,
                text: identity.displayName,
                data: identity,
                iconProps: {
                    render: () => (
                        <img className="created-by-filter-user-icon" src={identity.imageUrl} alt={identity.displayName} />
                    )
                }
            });
        });
        return options;
    }

    private _getResourcesListView(): JSX.Element {
        return (
            <Page className="flex-grow resources-list-view">
                <Header
                    title={Resources.ListViewTitle}
                    titleSize={TitleSize.Large}
                    commandBarItems={this._getViewCommandBarItems()}
                />
                <div className="resource-view-page-content flex flex-grow flex-column">
                    {this._getServiceConnectionUpgradeAvailable()}
                    {this._getFilterBar()}
                    <Card className="resources-container-card bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                        {this.state.resources.length === 0 && this._getNoResourcesView()}
                        {this.state.resources.length > 0 && this._getResourcesTableView()}
                    </Card>
                </div>
            </Page>
        );
    }

    private _getNewServiceConnectionPanel(): JSX.Element {
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.startScenario(PerformanceConstants.EndpointTypeSelectionScenario, true);
        return (
            <WrappedComponent
            dependencies={["ms.vss-distributedtask-web.dt-serviceendpoints-editor"]}
            wrappedType={"endpoints-editor-panel"}
            onDismiss = {() => {
                this._expandPanel.value = false;
            }}
            onRefresh = {() => this._actionCreator.initializeList(true)}
            onPrimaryAction = {() => {
                this._expandPanel.value = false;
                this._actionCreator.initializeList(true);
            }}
            contentType = { Constants.EndpointsEditorTypesViewType }
            panelTitle = { Resources.SelectEndpointTypesPanelTitle }
            disableAllPipelinesSetting = { !this.state.hasProjectLevelManagePermission }
            />
        );
    }

    private _getResourcesTableView(): JSX.Element {
        if (!this._areFiltersEmpty()) {
            Utils_Accessibility.announce(localeFormat(Resources.FilteredDataCountText, this.state.resources.length));
        }
        return (
            <Table
                columns={this._getTableColumns()}
                scrollable={true}
                itemProvider={this._resourcesProvider}
                onActivate={this._onActivateRow}
                showHeader={false}
                pageSize={this._pageSize}
            />
        );
    }

    private _getNoResourcesView(): JSX.Element {
        Utils_Accessibility.announce(Resources.ZeroFilteredDataPrimaryText);
        const image = getAssetLocation(this.context.pageContext, "ms.vss-distributedtask-web/dt-resources-hub/Content/no-results.png");

        return (
            <ZeroData
                className={"no-resources-container"}
                imagePath={image}
                imageAltText={"Image"}
                primaryText={Resources.ZeroFilteredDataPrimaryText}
            />
        );
    }

    private _getfilters() {
        let filterState = this._filter.getState();
        const keywordProp = filterState["keyword"];
        const keyword = keywordProp ? keywordProp.value : "";
        const createdByProp = filterState["createdByFilter"];
        const createdByFilter = createdByProp ? createdByProp.value : [];
        let filters: ResourceFilters = {
            createdBy: createdByFilter.map((filter: IListBoxItem<IdentityRef>) => { return filter.id; }),
            resourceType: [],
            searchText: keyword
        };

        return filters;
    }

    private _loadMoreData() {
        this._actionCreator.loadMoreData(this._getfilters(), this.state.continuationToken);
        this._pageSize = 1;
    }

    private _onFilterApplied = (value: IFilterState, action: string) => {
        let filters = this._getfilters();
        this._actionCreator.filterOptions(filters);
        this._resourcesSource.saveSettings(filters);
    }

    private _getTableColumns = (): ITableColumn<ResourceItemExtended>[] => {
        return [
            {
                id: "name",
                width: new ObservableValue(-100),
                name: "Name",
                className: "resource-name-cell",
                renderCell: this._renderNameCell,
                columnStyle: TableColumnStyle.Primary
            }
        ];
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<ResourceItemExtended>, tableItem: ResourceItemExtended): JSX.Element => {
        let currentSize = this.state.resources.length;
        let hasMoreData = (this.state.continuationToken !== null && this.state.continuationToken !== "");
        let displayName = Utils_String.equals(tableItem.properties["isDisabled"], "True", true) ? tableItem.name + Resources.DisabledTitleAppendix : tableItem.name;
        if (rowIndex === currentSize - 5 && hasMoreData && columnIndex === 0) {
            this._loadMoreData();
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <img className="resource-image" src={tableItem.iconUrl} alt="" role="presentation" />
                <div className="fontWeightSemiBold fontSizeM flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={tableItem.name}>
                        <span className="text-ellipsis body-m" aria-label={format(Resources.ServiceConnectionRowFormat, tableItem.resourceType, tableItem.name)}>{displayName}</span>
                    </Tooltip>
                    {tableItem.isShared && <Pill className="shared-button margin-left-4" excludeTabStop={true} size={PillSize.compact}>{Resources.ServiceConnectionSharedTag}</Pill>}
                    {tableItem.isOutdated && <Pill className="shared-button margin-left-4" excludeTabStop={true} size={PillSize.compact}>{Resources.ServiceConnectionOutdatedTag}</Pill>}
                    {this._allowCreatingEndpointsInDraftStateEnabled && Utils_String.equals(tableItem.properties["isDraft"], "True", true)
                        && <Pill className="margin-left-4" excludeTabStop={true} size={PillSize.compact}>{Resources.ServiceConnectionDraftPill}</Pill>}
                    {this._workloadIdentityFederationEnabled && Utils_String.equals(tableItem.properties["isSchemeUpgradeAvailable"], "True", true)
                        && <Icon iconName="LocationDot" className="scheme-conversion-available-icon margin-horizontal-4 font-weight-light" size={IconSize.medium} tooltipProps={{ text: Resources.ServiceConnectionSchemeUpgradeAvailableTooltip }} />}
                </div>
                {tableItem.issue && (
                    <Icon
                        className={tableItem.issue.type === CheckIssueType.Error ? "resource-error-icon" : "resource-warning-icon"}
                        iconName={tableItem.issue.type === CheckIssueType.Error ? "ErrorBadge" : "Warning"}
                        tooltipProps={{
                            text: tableItem.issue.message
                        }}
                    />
                )}
            </SimpleTableCell>
        );
    }

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITableRow<ResourceItemExtended>) => {
        // this._actionCreator.changeViewType(row.index, Constants.ViewType.ServiceEndpoint);
        this._selectedResourceIndex = row.index;
        this._viewType.value = Constants.ViewType.ServiceEndpoint;
    }

    private _getViewCommandBarItems(): IHeaderCommandBarItem[] {
        return [
            {
                id: "filter-command",
                iconProps: {
                    iconName: this.state.filterBarVisibility ? "FilterSolid" : "Filter"
                },
                subtle: true,
                onActivate: () => {
                    //This announcement happens before updating filterBarVisibility element
                    this.state.filterBarVisibility ? Utils_Accessibility.announce(Resources.FilterBarCollapsedText) : Utils_Accessibility.announce(Resources.FilterBarExpandedText);
                    this.setState({
                        filterBarVisibility: !this.state.filterBarVisibility
                    });
                },
                ariaLabel: Resources.FilterButton
            },
            {
                id: "new-service-connection",
                text: Resources.SelectEndpointTypesPanelTitle,
                important: true,
                onActivate: () => {
                    this._expandPanel.value = true;
                },
                ariaLabel: Resources.SelectEndpointTypesPanelTitle
            },
            {
                id: "service-connection-security",
                text: Resources.SecurityTabTitle,
                isPrimary: false,
                important: false,
                rank: 2,
                iconProps: {
                    iconName: "Lock"
                },
                onActivate: () => {
                    this._viewType.value = Constants.ViewType.SecurityTab;
                },
                ariaLabel: Resources.SecurityTabTitle
            }
        ];
    }

    private _getProjectLevelSecurityView() {
        const serviceEndpointsComponentProps: RoleAssignmentComponentSecurityControl.IRoleAssignmentSecurityProps = {
            serviceInstanceId: "00025394-6065-48ca-87d9-7f5672854ef7",
            scopeId: SecurityNamespaceConstants.ProjectRootScopeId.valueOf(),
            userId: null,
            resourceId: this._getResourceId(),
            manageRolesPermission: SecurityNamespaceConstants.AdminPermission.valueOf(),
            showAvatars: true,
            enableToggleInheritence: false,
            endpointId: null,
            canEditProjectView: this.state.hasProjectLevelManagePermission,
            className: "security-user-permissions-dialog",
            identityTypes: ["user", "group", "servicePrincipal"],
            operationScopes: ["ims", "source"],
            roleScope: "Project",
            enableTogglingOrganizationButton: false
        };
        return(
            <Page className="flex-grow">
                <Header
                    title={Resources.SecurityTabTitle}
                    titleSize={TitleSize.Large}
                    backButtonProps={{
                        onClick: this._onBackNavigation,
                        id: "security-page-back-button-id"
                    }}
                />
                <RoleAssignmentComponentSecurityControl.RoleAssignmentComponentSecurityControl {...serviceEndpointsComponentProps} />
            </Page>

        );
    }

    private _getResourceId(): string {
        const tfsPageData = getTFSPageData(this.context.pageContext);
        return tfsPageData.project.id;
    }

    private _getCreatedByListItems = (): IdentityRef[] => {
        return Object.keys(this._filterDefinition.identities).map(key => this._filterDefinition.identities[key]);
    }

    private _setfilterOptions(updatedState: IResourcesHubState) {
        if (!this._filter) {
            this._filter = new Filter();
            if (updatedState.filterOptions) {
                updatedState.filterOptions.identities.forEach(identity => {
                    if (identity) {
                    this._filterDefinition.identities[identity.id] = identity;
                    }
                });
            }

            if (updatedState.filters) {
                let selectedIdentities = updatedState.filters.createdBy;
                let identitiesDefaultValues: IdentityRef[] = [];
                selectedIdentities.forEach(identity => {
                    if (this._filterDefinition.identities[identity]) {
                        identitiesDefaultValues.push(this._filterDefinition.identities[identity]);
                    }
                });

                let currentState = {
                    createdByFilter: { value: identitiesDefaultValues },
                    keyword: { value: updatedState.filters.searchText }
                };

                this._filter.setState(currentState);
                this._filter.setDefaultState({});
            }

            this._filter.subscribe(this._onFilterApplied, FILTER_APPLIED_EVENT);
        }
    }

    private _areFiltersEmpty(): boolean {
        if (!this.state.filters
            || (this.state.filters.createdBy.length <= 0
                && this.state.filters.resourceType.length <= 0
                && !this.state.filters.searchText)) {
            return true;
        }

        return false;
    }

    private _getResourceIdFromUrl(): string {
        const historyState = this._historyService.getState();
        return historyState.resourceId;
    }

    private _setUrl(resourceId: string) {
        let historyState = this._historyService.getState();
        historyState.resourceId = resourceId;
        this._historyService.pushState(historyState);
    }

    private _onHistoryPopped = (popStateEvent: INavigationStateChangedEvent): void => {
        const { newState } = popStateEvent;
        if (newState && newState.state) {
            if (!newState.state.resourceId) {
                this._viewType.value = Constants.ViewType.Hub;
            } else {
                this._viewType.value = Constants.ViewType.ServiceEndpoint;
            }
        }
    }

    private _onBackNavigation = (): void => {
        let historyState = this._historyService.getState();
        delete historyState.resourceId;
        this._historyService.pushState(historyState);
        this._viewType.value = Constants.ViewType.Hub;
        this._actionCreator.initializeList(true);
    }

    private _historyService: IVssHistoryService;
    private _store: ResourcesStore;
    private _actionCreator: ResourcesActionsCreator;
    private _actionsHub: ResourcesActionsHub;
    private _resourcesProvider: ArrayItemProvider<ResourceItemExtended>;
    private _serviceEndpointStore: ServiceEndpointStore;
    private _serviceEndpointActionCreator: ServiceEndpointActionsCreator;
    private _filter: Filter;
    private _filterDefinition: IFilter = { "identities": {} };
    private _resourcesSource: ResourcesSource;
    private _pageSize = 10;
    private _ttiMarked: boolean;
    private _viewType: ObservableValue<Constants.ViewType>;
    private _expandPanel: ObservableValue<boolean>;
    private _selectedResourceIndex: number;
    private _selectionMultiList: DropdownMultiSelection;
}

VssComponent.register("dt-resources-hub", ResourcesHub);
